import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilePlus, Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { formatDateWithOptionsBs } from "@/lib/format";
import { todayYmd } from "@/lib/company-expiry";
import { COMPANY_INVOICE_STATUS_CONFIG } from "@/lib/status-configs";
import type { CompanyInvoiceSummary } from "@/types";
import { InvoiceDetailDialog } from "./InvoiceDetailDialog";
import { dueLabel, fmtKm, invoiceDisplayStatus, invoicePeriodLabel } from "./invoice-format";

/** Zadnjih 12 lokalnih mjeseci, od prošlog unazad (tekući se fakturiše privremenom fakturom). */
function recentMonths(today: string): Array<{ key: string; year: number; month: number; label: string }> {
  const [y, m] = today.split("-").map(Number) as [number, number];
  return Array.from({ length: 12 }, (_, i) => {
    const total = y * 12 + (m - 1) - (i + 1);
    const year = Math.floor(total / 12);
    const month = (total % 12) + 1;
    const label = formatDateWithOptionsBs(`${year}-${String(month).padStart(2, "0")}-15T12:00:00Z`, {
      month: "long",
      year: "numeric",
      timeZone: "Europe/Sarajevo",
    });
    return { key: `${year}-${month}`, year, month, label };
  });
}

export function CompanyInvoicesSection({
  companyId,
  hideWhenEmpty = false,
}: {
  companyId: string;
  /** Firma na kreditu: sekcija se vidi samo ako ima faktura (za dopune kredita ili iz vremena na fakturi). */
  hideWhenEmpty?: boolean;
}) {
  const queryClient = useQueryClient();
  const today = todayYmd();
  const months = useMemo(() => recentMonths(today), [today]);
  const [monthKey, setMonthKey] = useState(months[0]!.key);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["admin", "company", companyId, "invoices"],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${companyId}/invoices`);
      await throwIfNotOk(res, "Greška pri učitavanju faktura");
      return res.json() as Promise<CompanyInvoiceSummary[]>;
    },
  });

  const createDraft = useMutation({
    mutationFn: async () => {
      const m = months.find((x) => x.key === monthKey)!;
      const res = await fetch(`/api/companies/${companyId}/invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year: m.year, month: m.month }),
      });
      await throwIfNotOk(res, "Greška pri pravljenju nacrta");
      return res.json() as Promise<{ invoiceId: string; created: boolean }>;
    },
    onSuccess: (r) => {
      toast.success(r.created ? "Nacrt napravljen" : "Faktura za taj mjesec već postoji");
      queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId, "invoices"] });
      setOpenId(r.invoiceId);
    },
  });

  if (hideWhenEmpty && !isLoading && invoices.length === 0) return null;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="size-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Fakture</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={monthKey} onValueChange={setMonthKey}>
            <SelectTrigger className="h-8 w-44" aria-label="Mjesec za nacrt">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" disabled={createDraft.isPending} onClick={() => createDraft.mutate()}>
            <FilePlus className="size-4" /> Napravi nacrt
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-4 h-24 animate-pulse rounded-lg bg-muted" />
      ) : invoices.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Još nema faktura. Nacrt za prošli mjesec nastaje automatski prvog u mjesecu kad postoje seanse za fakturisanje.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border/60">
                <th className="py-2 pr-3 text-left font-medium">Broj</th>
                <th className="py-2 pr-3 text-left font-medium">Period</th>
                <th className="py-2 pr-3 text-right font-medium">Iznos</th>
                <th className="py-2 pr-3 text-left font-medium">Rok</th>
                <th className="py-2 pr-3 text-left font-medium">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const cfg = COMPANY_INVOICE_STATUS_CONFIG[invoiceDisplayStatus(inv, today)];
                return (
                  <tr key={inv.id} className="border-b border-border/40 last:border-0">
                    <td className="py-2.5 pr-3 font-medium">{inv.invoiceNumber ?? <span className="text-muted-foreground">Nacrt</span>}</td>
                    <td className="py-2.5 pr-3" title={`${inv.lineCount} stavki`}>
                      {invoicePeriodLabel(inv)}
                      {inv.kind === "interim" && <span className="ml-1.5 text-xs text-muted-foreground">privremena</span>}
                    </td>
                    <td className={cn("py-2.5 pr-3 text-right tabular-nums", inv.status === "void" && "text-muted-foreground line-through")}>
                      {fmtKm(inv.total)}
                    </td>
                    <td className="py-2.5 pr-3 text-muted-foreground">{dueLabel(inv.dueAt)}</td>
                    <td className="py-2.5 pr-3">
                      <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold", cfg.bg, cfg.color)}>
                        <span className={cn("size-1.5 rounded-full", cfg.dot)} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="py-2.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setOpenId(inv.id)}>Otvori</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openId && <InvoiceDetailDialog invoiceId={openId} companyId={companyId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
